import { z } from 'zod';

import { ImageSizeWithOriginalInUriSchemaBuilder } from '../../utils/image-size';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';
import { IntegrationMetaSchema, StrictTaxSchema, StrictTaxWithNameSchema } from '../experience/experiences';

export enum AddOnStatus {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
}

export enum AddOnImageSize {
  W360 = '360',
  W720 = '720',
  W1080 = '1080',
}
export const AddOnImageSizeWithOriginalInUriSchema = ImageSizeWithOriginalInUriSchemaBuilder(Object.values(AddOnImageSize));

export const AddOnImageSizeMediaSchema = z.object({
  id: z.string().uuid(),
  uri: AddOnImageSizeWithOriginalInUriSchema,
});

export const BaseAddOnSchema = z.strictObject({
  id: z.string().uuid(),
  price: z.number().finite().nonnegative(),
  label: z.string().nonempty(),
  description: z.string(),
  status: z.nativeEnum(AddOnStatus),
  integrationMeta: z.array(IntegrationMetaSchema),
});

export const RequestAddOnSchema = BaseAddOnSchema.partial({ id: true }).extend({
  pictureId: z.string().uuid().nullable(),
  taxes: z.array(StrictTaxSchema),
});

export const ResponseAddOnSchema = BaseAddOnSchema.extend({
  picture: AddOnImageSizeMediaSchema.nullable(),
  taxes: z.array(StrictTaxWithNameSchema),
});

const GetAddOnsQuerySchema = z
  .object({
    status: z.nativeEnum(AddOnStatus),
    language: z.string().optional().nullable(),
  })
  .partial()
  .strict();
export const GetAddOnsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetAddOnsQuerySchema> }) =>
    `v1/brands/${brandId}/add-ons?${buildQueryString(query)}`,
  responseSchema: z.object({ items: z.array(ResponseAddOnSchema) }).strict(),
  requestSchema: z.void(),
  querySchema: GetAddOnsQuerySchema,
});

const PatchAddOnsRequestSchema = BaseAddOnSchema.pick({ label: true }).required({ label: true }).strict();

export const DeleteAddOnEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, addOnId }: { brandId: string; addOnId: string }) => `v1/brands/${brandId}/add-ons/${addOnId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const PatchAddOnEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, addOnId }: { brandId: string; addOnId: string }) => `v1/brands/${brandId}/add-ons/${addOnId}`,
  responseSchema: z.void(),
  requestSchema: PatchAddOnsRequestSchema,
});

export enum AddOnField {
  LABEL = 'label',
  DESCRIPTION = 'description',
}
